import { useState } from 'react';
import api from '../services/Api';

const useSupportApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch support tickets with optional status filter
  const fetchTickets = async status => {
    setLoading(true);
    setError(null);
    try {
      const params = status ? { status } : {};
      const response = await api.get('/support', { params });

      // Fetch replies for each ticket
      const ticketsWithReplies = await Promise.all(
        response.data.map(async ticket => {
          try {
            const repliesResponse = await api.get(`/support/reply/${ticket._id}`);
            return {
              ...ticket,
              replies: repliesResponse.data,
            };
          } catch (err) {
            console.error(`Failed to fetch replies for ticket ${ticket._id}:`, err);
            return ticket;
          }
        })
      );

      return ticketsWithReplies;
    } catch (err) {
      setError('Failed to fetch tickets');
      console.error('Error fetching tickets:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get a specific ticket by ID
  const getTicketById = async id => {
    setLoading(true);
    setError(null);
    try {
      // Fetch ticket details
      const ticketResponse = await api.get(`/support/${id}`);

      // Fetch replies for this ticket
      const repliesResponse = await api.get(`/support/reply/${id}`);

      // Combine ticket details with replies
      return {
        ...ticketResponse.data,
        replies: repliesResponse.data,
      };
    } catch (err) {
      setError('Failed to fetch ticket details');
      console.error('Error fetching ticket:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Reply to a ticket
  const replyToTicket = async (supportId, replyData, adminId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/support/reply', {
        supportId,
        adminId,
        businessId: replyData.businessId,
        subject: replyData.subject,
        message: replyData.message || '',
        file: replyData.file || null,
      });
      return response.data;
    } catch (err) {
      setError('Failed to send reply');
      console.error('Error replying to ticket:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a ticket
  const deleteTicket = async id => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/support/${id}`);
      return true;
    } catch (err) {
      setError('Failed to delete ticket');
      console.error('Error deleting ticket:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (id, status) => {
    setLoading(true);
    setError(null);
    try {
      await api.patch(`/support/${id}`, { status });
      return true;
    } catch (err) {
      setError('Failed to update ticket status');
      console.error('Error updating ticket status:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchTickets,
    getTicketById,
    replyToTicket,
    deleteTicket,
    updateTicketStatus,
  };
};

export default useSupportApi;
